import { isNil } from 'lodash-es';

import { DateTimeFormat } from 'enums/dateTimeFormat';
import { NONE_LIST_OPTION } from 'utils/formatting/noneListOption';
import { translate } from 'utils/intlUtils';

import type { Duration } from 'store/client/graphql';
import { DurationUnit } from 'store/client/graphql';

import { getCurrentLocalDateTime, getDateTime, getFormattedDateTimeString } from './dateUtils';
import { localeStorage } from './storage/intl';

const { HOUR_MINUTE_SEPARATOR, TIME_TYPE_FORMAT } = DateTimeFormat;

export enum MeridiemIndicator {
  AM = 'a.m.',
  PM = 'p.m.',
}

export type TimeObject = {
  /**
   * Number of hours, currently 12hr clock
   */
  hour: number;
  /**
   * AM/PM
   */
  meridiem: MeridiemIndicator;
  /**
   * Number of minutes
   */
  minute: number;
};

/**
 * Converts a timestamp of format `DateTimeFormat.TIME_STAMP_FORMAT` to a `TimeObject`.
 */
export const getTimeObjectFromTimestamp = (time?: null | string): null | TimeObject => {
  const timeValues = time?.split?.(/:| /) || [];
  if (timeValues.length !== 3) {
    return null;
  }

  return {
    hour: Number.parseInt(timeValues[0]),
    meridiem: timeValues[2] as MeridiemIndicator,
    minute: Number.parseInt(timeValues[1]),
  };
};

/**
 * Converts a `TimeObject` to a timestamp of format `DateTimeFormat.TIME_STAMP_FORMAT`.
 */
export const getTimestampFromTimeObject = (time?: null | TimeObject): string => {
  if (!time) {
    return '';
  }

  const formattedTimeComponents = [time.hour, time.minute].map(timePart => timePart.toString().padStart(2, '0'));

  return `${formattedTimeComponents[0]}${HOUR_MINUTE_SEPARATOR}${formattedTimeComponents[1]} ${time.meridiem}`;
};

export const getJSDateFromTimestamp = (timestamp: string, date: string) => {
  const dateTime = getDateTime(`${date} ${timestamp}`, TIME_TYPE_FORMAT);

  return dateTime ? getFormattedDateTimeString(dateTime) : null;
};

export const durationOptions = {
  fifteen_min_before: { amount: 15, unit: DurationUnit.MINUTES },
  five_min_before: { amount: 5, unit: DurationUnit.MINUTES },
  one_hour_before: { amount: 1, unit: DurationUnit.HOURS },
  one_week_before: { amount: 1, unit: DurationUnit.WEEKS },
  ten_min_before: { amount: 10, unit: DurationUnit.MINUTES },
  thirty_min_before: { amount: 30, unit: DurationUnit.MINUTES },
  two_hour_before: { amount: 2, unit: DurationUnit.HOURS },
} as const;

/**
 * Get the ID of a duration input type from the pre-defined list of available duration options. If the duration
 * is null, then return the NONE_LIST_OPTION id (special list option constant used to clear the field)
 *
 * @param {Duration} duration - The Duration value (includes amount + unit)
 */
export const getDurationId = (
  duration: Duration | null
): (typeof NONE_LIST_OPTION)['name'] | keyof typeof durationOptions => {
  if (duration) {
    const durationOptionKeys = Object.keys(durationOptions) as (keyof typeof durationOptions)[];
    const selectedDurationOptionKey = durationOptionKeys.find(
      option => durationOptions[option]?.amount === duration?.amount && durationOptions[option]?.unit === duration?.unit
    );

    return selectedDurationOptionKey || NONE_LIST_OPTION.name;
  }

  return NONE_LIST_OPTION.name;
};

export const getDurationLabel = (duration: Duration) => {
  if (!duration) {
    return translate.t('none');
  }
  const label = getDurationId(duration);

  return label ? translate.t(label) : `${duration?.amount} ${duration?.unitName}`;
};

export const DEFAULT_SEARCH_DEBOUNCE = 250;

/**
 * Get a string representation of a DateTime relative to now, following our custom specification
 * e.g. If today is 'Thu, 06 Feb 2020 13:35:55 GMT' then
 *      getRelativeTime('2020-02-01T20:10:00.000Z') -> 4 days ago`
 * @param options.todayInsteadOfNow Return dates within today as "Today", instead of one minute before "Now".
 */

export const getRelativeTime = (date: string | undefined, options?: { todayInsteadOfNow: boolean }) => {
  const dateTime = getDateTime(date);
  const relativeTimeString = dateTime?.setLocale(localeStorage.get() || '').toRelative();
  if (!dateTime || isNil(relativeTimeString)) {
    return null;
  }

  const currentDateTime = getCurrentLocalDateTime();
  const oneMinuteAgo = currentDateTime.minus({ seconds: 60 });
  const oneWeekAgo = currentDateTime.minus({ days: 7 });
  const { t } = translate;

  const oneMinuteAgoDiff = oneMinuteAgo.diff(dateTime, 'seconds').toObject()?.seconds;
  const oneWeekAgoDiff = oneWeekAgo.diff(dateTime, 'days').toObject()?.days;

  if (
    options?.todayInsteadOfNow &&
    dateTime <= currentDateTime.endOf('day') &&
    dateTime >= currentDateTime.startOf('day')
  ) {
    return t('today');
  }
  if (!options?.todayInsteadOfNow && Number(oneMinuteAgoDiff) >= -120 && Number(oneMinuteAgoDiff) <= 0) {
    return t('now');
  }
  if (Number(oneWeekAgoDiff) >= -14 && Number(oneWeekAgoDiff) <= 0) {
    return relativeTimeString;
  }

  return getFormattedDateTimeString(dateTime.toISO(), DateTimeFormat.DATE_YEAR_FORMAT) || null;
};
